import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getForumPosts, resolveForumImageUrl, toggleForumLike } from "../../api/axios";
import { doctorImageUrl } from "../../api/imageUrl";
import { useAuth } from "../../auth/AuthContext";
import { timeAgo } from "../../utils/timeAgo";

const TYPES = [
  { value: "", label: "All" },
  { value: "question", label: "Question" },
  { value: "case_report", label: "Case report" },
  { value: "image", label: "Image" },
  { value: "discussion", label: "Discussion" },
];

const TYPE_STYLES = {
  question: "bg-blue-50 text-blue-700 border-blue-100",
  case_report: "bg-purple-50 text-purple-700 border-purple-100",
  image: "bg-pink-50 text-pink-700 border-pink-100",
  discussion: "bg-amber-50 text-amber-700 border-amber-100",
};

function Avatar({ name, image }) {
  const src = doctorImageUrl(image);
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="w-11 h-11 rounded-full object-cover shrink-0 border border-gray-100"
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  }
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="w-11 h-11 rounded-full bg-[#00342b] text-white flex items-center justify-center font-bold shrink-0">
      {initial}
    </div>
  );
}

const HeartIcon = ({ filled }) => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2}>
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CommentIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function ForumList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      const res = await getForumPosts({ type, q });
      if (active) { setPosts(res.data || []); setLoading(false); }
    })();
    return () => { active = false; };
  }, [type, q]);

  const filtered = useMemo(() => posts, [posts]);

  const handleLike = async (e, post) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) { toast.error("Login to like posts"); return; }

    const wasLiked = post.liked;
    setPosts((prev) => prev.map((p) => p.id === post.id
      ? { ...p, liked: !wasLiked, like_count: (p.like_count || 0) + (wasLiked ? -1 : 1) }
      : p));

    try {
      const res = await toggleForumLike(post.id);
      setPosts((prev) => prev.map((p) => p.id === post.id ? { ...p, liked: res.liked, like_count: res.like_count } : p));
    } catch (err) {
      setPosts((prev) => prev.map((p) => p.id === post.id ? { ...p, liked: wasLiked, like_count: post.like_count } : p));
      toast.error(err.message || "Failed to like post");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Forum</p>
          <h1 className="text-3xl font-extrabold text-[#00342b]">Case reports, questions, and opinions</h1>
          <p className="text-sm text-gray-500 mt-1">{posts.length} {posts.length === 1 ? "post" : "posts"} from the community</p>
        </div>
        {user && <Link to="/forum/create" className="px-5 py-3 rounded-xl bg-[#00342b] text-white font-semibold hover:bg-[#004d40] transition shrink-0">Create post</Link>}
      </div>

      <div className="flex gap-3 flex-wrap mb-6 border-b pb-4">
        {TYPES.map((t) => (
          <button
            key={t.value || "all"}
            onClick={() => setType(t.value)}
            className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${type === t.value ? "bg-[#00342b] text-white border-[#00342b]" : "bg-white text-gray-600 hover:border-gray-300"}`}
          >
            {t.label}
          </button>
        ))}
        <input
          className="px-4 py-2 rounded-full border flex-1 min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[#00342b]/20"
          placeholder="Search forum"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="grid gap-4">
        {loading && (
          <div className="p-10 text-center text-gray-400 bg-white border rounded-3xl">Loading conversations…</div>
        )}

        {!loading && filtered.map((p) => {
          const image = p.image_url ? resolveForumImageUrl(p.image_url) : null;
          return (
            <div
              key={p.id}
              onClick={() => navigate(`/forum/${p.id}`)}
              className="bg-white border rounded-3xl p-5 hover:shadow-md hover:-translate-y-0.5 transition cursor-pointer group"
            >
              <div className="flex gap-4">
                <Avatar name={p.author_name} image={p.author_image} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span className="font-semibold text-gray-700">{p.author_name || "Member"}</span>
                    {p.author_specialty && <><span>·</span><span>{p.author_specialty}</span></>}
                    <span>·</span>
                    <span>{timeAgo(p.created_at)}</span>
                  </div>

                  <div className="flex items-start justify-between gap-4 mt-2">
                    <div className="min-w-0">
                      <span className={`inline-block text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border mb-1.5 ${TYPE_STYLES[p.type] || "bg-gray-50 text-gray-600 border-gray-100"}`}>
                        {(p.type || "discussion").replace("_", " ")}
                      </span>
                      <h2 className="text-lg md:text-xl font-bold text-gray-900 group-hover:text-[#00342b] transition">{p.title}</h2>
                      <p className="text-gray-600 mt-1.5 line-clamp-2 text-sm">{p.content}</p>
                    </div>
                    {image && (
                      <img
                        src={image}
                        alt={p.title}
                        className="w-24 h-24 rounded-2xl object-cover shrink-0 hidden sm:block"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                  </div>

                  <div className="flex items-center gap-5 mt-4 text-sm text-gray-500">
                    <button
                      onClick={(e) => handleLike(e, p)}
                      className={`flex items-center gap-1.5 transition hover:text-rose-600 ${p.liked ? "text-rose-600" : ""}`}
                    >
                      <HeartIcon filled={p.liked} />
                      <span className="font-medium">{p.like_count || 0}</span>
                    </button>
                    <span className="flex items-center gap-1.5">
                      <EyeIcon />
                      <span className="font-medium">{p.views || 0}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <CommentIcon />
                      <span className="font-medium">{p.comment_count || 0}</span> replies
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {!loading && !filtered.length && (
          <div className="p-10 text-center text-gray-500 bg-white border rounded-3xl">
            No forum posts yet. {user ? <Link to="/forum/create" className="text-[#00342b] font-semibold underline">Be the first to post.</Link> : "Be the first to post."}
          </div>
        )}
      </div>
    </div>
  );
}
